import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ExternalLink } from "lucide-react";
import { api, formatApiError } from "../../lib/apiClient";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

function Row({ it, onDone }) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const verify = async (link, fromSuggestion) => {
    if (!link && !window.confirm(`¿Verificar ${it.alias} sin enlace oficial? El cliente la verá validada pero sin fuente enlazada.`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/normativa/${it.id}/validate`, { url_oficial: link || undefined, from_suggestion: fromSuggestion });
      toast.success(`${it.alias} verificada`);
      onDone();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(e?.response?.status === 422 && Array.isArray(d)
        ? "Enlace no válido: usa una URL completa que empiece por http:// o https://"
        : formatApiError(d) || "No se pudo verificar");
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className="py-3 border-t border-gray-100" data-testid={`unverified-norm-${it.id}`}>
      <p className="text-sm text-gray-900 font-medium">{it.alias}</p>
      {it.suggested_url ? (
        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs" data-testid={`unverified-norm-suggestion-${it.id}`}>
          <span className="text-gray-500">Sugerencia ({it.suggested_source}):</span>
          <a href={it.suggested_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-1 break-all" data-testid={`unverified-norm-suggestion-link-${it.id}`}>
            {it.suggested_url} <ExternalLink className="w-3 h-3 shrink-0" />
          </a>
          <Button size="sm" onClick={() => verify(it.suggested_url, true)} disabled={busy} className="h-7 rounded-sm text-xs" data-testid={`unverified-norm-confirm-suggestion-${it.id}`}>
            {busy ? "…" : "Confirmar sugerencia"}
          </Button>
        </div>
      ) : (
        <p className="mt-1 text-xs text-amber-700" data-testid={`unverified-norm-no-suggestion-${it.id}`}>Sin enlace sugerido: búscalo en el Gestor Normativo y pégalo abajo.</p>
      )}
      <div className="mt-2 grid md:grid-cols-[2fr_auto] gap-2 items-center">
        <label className="sr-only" htmlFor={`url-${it.id}`}>Otro enlace oficial</label>
        <Input id={`url-${it.id}`} value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Otro enlace oficial (opcional)"
          className="h-8 text-xs rounded-sm placeholder:text-gray-400" data-testid={`unverified-norm-url-${it.id}`} />
        <Button size="sm" variant="outline" onClick={() => verify(url.trim(), false)} disabled={busy} className="h-8 rounded-sm text-xs" data-testid={`unverified-norm-verify-${it.id}`}>
          {url.trim() ? "Verificar con este enlace" : "Verificar sin enlace"}
        </Button>
      </div>
    </li>
  );
}

export function UnverifiedNormsSection() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/admin/normativa/unverified").then(({ data }) => setItems(data.items || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  if (!items) return null;
  const withSug = items.filter((i) => i.suggested_url).length;
  return (
    <section className="border border-gray-200 bg-white p-5" data-testid="unverified-norms-section">
      <h2 className="text-lg font-semibold">Lista base sin verificar ({items.length})</h2>
      <p className="text-xs text-gray-500 mb-2" data-testid="unverified-norms-summary">
        {withSug} con enlace oficial sugerido. Ábrelo, compruébalo y pulsa «Confirmar sugerencia»: se registra tu nombre y la fecha, y el cliente verá la norma como validada con su enlace.
      </p>
      {items.length === 0 ? <p className="text-sm text-gray-600">Todas las normas base están verificadas.</p>
        : <ul>{items.map((it) => <Row key={it.id} it={it} onDone={load} />)}</ul>}
    </section>
  );
}
